import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Badge, Button, Form, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { FiSearch, FiFilter, FiPlus, FiFileText, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

export default function Invoices() {
    const [invoices, setInvoices] = useState({ data: [], current_page: 1, last_page: 1, total: 0 });
    const [totalPrice, setTotalPrice] = useState(0);
    const [typeFilter, setTypeFilter] = useState('');
    const [searchString, setSearchString] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const context = useContext(AppContext);
    const navigate = useNavigate();

    // Load invoices
    const loadInvoices = (page = 1) => {
        setIsLoading(true);
        tre.api('GET', `/companies/${context.idCompany}/invoices`, {
            context: context,
            params: {
                page: page,
                rows: 20,
                type: typeFilter,
                search: searchString
            },
            success: (r) => {
                setInvoices(r.invoices);
                setTotalPrice((Math.round(r.totalPrice * 100) / 100).toFixed(2));
                setCurrentPage(page);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    useEffect(() => {
        loadInvoices(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeFilter]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            loadInvoices(1);
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchString]);

    const onSelectInvoice = (inv) => {
        navigate(`/invoice/${inv.id}`);
    };

    // Pagination
    const totalPages = invoices.last_page || 1;
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            loadInvoices(page);
        }
    };

    // -----| Render |-----
    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiFileText className="me-2" style={{ color: '#1a73e8' }} />
                        Facturas y Presupuestos
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {invoices.total || 0} documentos en total
                    </p>
                </div>
                <Button variant="primary" onClick={() => navigate('/invoice/create')}>
                    <FiPlus className="me-2" />
                    Nueva Factura
                </Button>
            </div>

            {/* Filters - Inline style */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Search */}
                <div className="position-relative" style={{ minWidth: '280px', flex: 1, maxWidth: '400px' }}>
                    <FiSearch
                        className="position-absolute"
                        style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5f6368' }}
                    />
                    <Form.Control
                        type="text"
                        placeholder="Buscar por número o cliente..."
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        style={{ paddingLeft: '38px', backgroundColor: 'white' }}
                    />
                </div>

                {/* Type filter pills */}
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted me-1" style={{ fontSize: '14px' }}>Tipo:</span>
                    <Button
                        size="sm"
                        variant={typeFilter === '' ? 'primary' : 'light'}
                        onClick={() => setTypeFilter('')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Todos
                    </Button>
                    <Button
                        size="sm"
                        variant={typeFilter === 'INVOICE' ? 'primary' : 'light'}
                        onClick={() => setTypeFilter('INVOICE')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Facturas
                    </Button>
                    <Button
                        size="sm"
                        variant={typeFilter === 'BUDGET' ? 'info' : 'light'}
                        onClick={() => setTypeFilter('BUDGET')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Presupuestos
                    </Button>
                </div>

                {/* Clear filters */}
                {(searchString || typeFilter) && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => { setSearchString(''); setTypeFilter(''); }}
                        style={{ color: '#ea4335', textDecoration: 'none' }}
                    >
                        <FiFilter className="me-1" /> Limpiar
                    </Button>
                )}
            </div>

            {/* Table Card */}
            <Card>
                {/* Top Pagination Info */}
                {!isLoading && invoices.data && invoices.data.length > 0 && (
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                <strong style={{ color: '#202124' }}>{invoices.total || 0}</strong> registros
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Mostrando <strong style={{ color: '#202124' }}>{invoices.from || 0}</strong> - <strong style={{ color: '#202124' }}>{invoices.to || 0}</strong>
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                            </span>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(1)}>
                                    Primera
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                                    <FiChevronLeft className="me-1" /> Anterior
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                                    Siguiente <FiChevronRight className="ms-1" />
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>
                                    Última
                                </Button>
                            </div>
                        )}
                    </Card.Header>
                )}

                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#1a73e8' }} />
                            <p className="mt-3 text-muted">Cargando facturas...</p>
                        </div>
                    ) : invoices.data && invoices.data.length > 0 ? (
                        <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>Nº FACTURA</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>FECHA</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>TIPO</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CLIENTE</th>
                                    <th className="py-3 px-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>TOTAL</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.data.map((inv) => (
                                    <tr
                                        key={inv.id}
                                        onClick={() => onSelectInvoice(inv)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td className="py-3 px-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        backgroundColor: inv.type === 'BUDGET' ? '#e8f4fd' : '#e8f0fe',
                                                        color: inv.type === 'BUDGET' ? '#4285f4' : '#1a73e8'
                                                    }}
                                                >
                                                    <FiFileText size={16} />
                                                </div>
                                                <span style={{ color: '#202124', fontWeight: 600 }}>
                                                    {inv.num_invoice || '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <span className="text-muted" style={{ fontSize: '14px' }}>
                                                {moment(inv.date).format('DD MMM YYYY')}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <Badge
                                                bg={inv.type === 'BUDGET' ? 'info' : 'primary'}
                                                style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}
                                            >
                                                {inv.type === 'BUDGET' ? 'Presupuesto' : 'Factura'}
                                            </Badge>
                                        </td>
                                        <td className="py-3">
                                            <span style={{ color: '#202124' }}>
                                                {inv.customer ? inv.customer.name : '-'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-3 text-end">
                                            <span style={{ fontWeight: 600, color: '#202124' }}>
                                                {(Number(inv.total) || 0).toFixed(2)} €
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-5">
                            <FiFileText size={48} className="mb-3" style={{ color: '#dadce0' }} />
                            <p className="text-muted">No se encontraron facturas</p>
                        </div>
                    )}
                </Card.Body>

                {/* Footer with total */}
                {invoices.data && invoices.data.length > 0 && (
                    <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            {totalPages > 1 && (
                                <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                    Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                                </span>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>Total Facturas:</span>
                            <span style={{ fontSize: '18px', fontWeight: 700, color: '#1a73e8' }}>{totalPrice} €</span>
                        </div>
                    </Card.Footer>
                )}
            </Card>
        </div>
    );
}
